<div class="flex h-full min-h-0 flex-col gap-6 overflow-y-auto">
	<!-- Header -->
	<div class="flex flex-wrap items-start justify-between gap-3">
		<div>
			<h1 class="text-3xl font-bold" [translate]>Cron Jobs</h1>
			<p class="text-base-content/60 mt-2 text-sm" [translate]>
				View, add, remove, and manually run scheduled cron jobs. All changes affect the system crontab of the
				server process.
			</p>
		</div>
		<div class="flex flex-wrap items-center gap-2">
			<button
				class="btn btn-sm btn-outline"
				(click)="refresh()"
			>
				<span [translate]>Refresh</span>
			</button>
			<button
				class="btn btn-sm btn-outline"
				[class.btn-active]="showPredefinedPanel()"
				(click)="togglePredefinedPanel()"
			>
				<span [translate]>Add predefined job</span>
			</button>
			<button
				class="btn btn-sm btn-primary"
				[class.btn-active]="showAddForm()"
				(click)="toggleAddForm()"
			>
				<span [translate]>Add custom job</span>
			</button>
		</div>
	</div>

	<!-- Add custom job form -->
	@if (showAddForm()) {
		<div class="bg-base-100 border-base-200 rounded-xl border p-5 shadow-sm">
			<h2
				class="mb-4 text-lg font-semibold"
				[translate]
			>
				Add custom cron job
			</h2>
			<div class="grid gap-4 md:grid-cols-2">
				<div class="form-control gap-1">
					<label class="label">
						<span
							class="label-text font-medium"
							[translate]
							>Schedule</span
						>
					</label>
					<input
						type="text"
						class="input input-bordered input-sm font-mono"
						placeholder="0 8 * * *"
						[value]="newSchedule()"
						(input)="newSchedule.set($any($event.target).value)"
					/>
					<span class="text-base-content/50 text-xs" [translate]>minute hour day month weekday</span>
				</div>
				<div class="form-control gap-1">
					<label class="label">
						<span
							class="label-text font-medium"
							[translate]
							>Comment (optional)</span
						>
					</label>
					<input
						type="text"
						class="input input-bordered input-sm"
						placeholder="my scheduled job"
						[value]="newComment()"
						(input)="newComment.set($any($event.target).value)"
					/>
				</div>
				<div class="form-control col-span-full gap-1">
					<label class="label">
						<span
							class="label-text font-medium"
							[translate]
							>Command</span
						>
					</label>
					<input
						type="text"
						class="input input-bordered input-sm font-mono"
						placeholder="/bin/bash -lc 'cd /app && uv run python script.py'"
						[value]="newCommand()"
						(input)="newCommand.set($any($event.target).value)"
					/>
				</div>
				<div class="form-control col-span-full">
					<label class="label cursor-pointer justify-start gap-3">
						<input
							type="checkbox"
							class="checkbox checkbox-sm"
							[checked]="newWithLogFile()"
							(change)="newWithLogFile.set($any($event.target).checked)"
						/>
						<span
							class="label-text"
							[translate]
							>Redirect output to a log file (enables log viewer)</span
						>
					</label>
				</div>
			</div>
			<div class="mt-4 flex justify-end gap-2">
				<button
					class="btn btn-sm btn-ghost"
					(click)="toggleAddForm()"
				>
					<span [translate]>Cancel</span>
				</button>
				<button
					class="btn btn-sm btn-primary"
					[disabled]="!newSchedule() || !newCommand() || isAdding()"
					(click)="addJob()"
				>
					@if (isAdding()) {
						<span class="loading loading-spinner loading-xs"></span>
					}
					<span [translate]>Add job</span>
				</button>
			</div>
		</div>
	}

	<!-- Add predefined job panel -->
	@if (showPredefinedPanel()) {
		<div class="bg-base-100 border-base-200 rounded-xl border p-5 shadow-sm">
			<h2
				class="mb-4 text-lg font-semibold"
				[translate]
			>
				Add predefined cron job
			</h2>

			@if (predefinedJobs().length === 0) {
				<p
					class="text-base-content/60 text-sm"
					[translate]
				>
					No predefined jobs available.
				</p>
			} @else {
				<div class="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
					@for (predefined of predefinedJobs(); track trackByPredefinedId($index, predefined)) {
						<button
							class="border-base-200 rounded-xl border p-4 text-left transition-colors"
							[class.border-primary]="selectedPredefined()?.id === predefined.id"
							[class.bg-primary]="selectedPredefined()?.id === predefined.id"
							[class.text-primary-content]="selectedPredefined()?.id === predefined.id"
							[class.bg-base-100]="selectedPredefined()?.id !== predefined.id"
							[class.hover:border-primary]="selectedPredefined()?.id !== predefined.id"
							(click)="selectPredefined(predefined)"
						>
							<div class="font-semibold">{{ predefined.name }}</div>
							<div
								class="mt-1 text-xs opacity-70"
								[class.opacity-80]="selectedPredefined()?.id === predefined.id"
							>
								{{ predefined.description }}
							</div>
							<code class="mt-2 block text-xs opacity-60">{{ predefined.schedule }}</code>
						</button>
					}
				</div>

				@if (selectedPredefined(); as predefined) {
					<div class="border-base-200 rounded-xl border p-4">
						<h3 class="mb-3 font-semibold">{{ predefined.name }}</h3>

						<div class="grid gap-4 md:grid-cols-2">
							<div class="form-control gap-1">
								<label class="label">
									<span
										class="label-text font-medium"
										[translate]
										>Schedule</span
									>
								</label>
								<input
									type="text"
									class="input input-bordered input-sm font-mono"
									[value]="predefinedScheduleOverride()"
									(input)="predefinedScheduleOverride.set($any($event.target).value)"
								/>
							</div>
							@for (param of predefined.requiredParams; track param) {
								<div class="form-control gap-1">
									<label class="label">
										<span class="label-text font-medium font-mono">{{ param }}</span>
									</label>
									<input
										type="text"
										class="input input-bordered input-sm font-mono"
										[placeholder]="getParamPlaceholder(param)"
										[value]="predefinedParams()[param]"
										(input)="setPredefinedParam(param, $any($event.target).value)"
									/>
								</div>
							}
						</div>

						<div class="mt-4 flex justify-end gap-2">
							<button
								class="btn btn-sm btn-ghost"
								(click)="togglePredefinedPanel()"
							>
								<span [translate]>Cancel</span>
							</button>
							<button
								class="btn btn-sm btn-primary"
								[disabled]="isAddingPredefined() || !arePredefinedParamsComplete()"
								(click)="addPredefinedJob()"
							>
								@if (isAddingPredefined()) {
									<span class="loading loading-spinner loading-xs"></span>
								}
								<span [translate]>Add job</span>
							</button>
						</div>
					</div>
				}
			}
		</div>
	}

	<!-- Jobs list -->
	<div class="bg-base-100 border-base-200 rounded-xl border shadow-sm">
		@if (isLoading()) {
			<div class="flex items-center gap-3 p-6">
				<span class="loading loading-spinner loading-sm"></span>
				<span
					class="text-base-content/60 text-sm"
					[translate]
					>Loading cron jobs…</span
				>
			</div>
		} @else if (loadError()) {
			<div
				role="alert"
				class="alert alert-error m-4"
			>
				<span>{{ loadError() }}</span>
			</div>
		} @else if (jobs().length === 0) {
			<div class="py-10 text-center">
				<p
					class="text-base-content/60 text-sm"
					[translate]
				>
					No cron jobs found in the system crontab.
				</p>
			</div>
		} @else {
			<div class="overflow-x-auto">
				<table class="table w-full">
					<thead class="bg-base-200/50 text-base-content/60 text-xs font-bold tracking-wider uppercase">
						<tr>
							<th [translate]>Schedule</th>
							<th [translate]>Command</th>
							<th [translate]>Comment</th>
							<th [translate]>Log file</th>
							<th class="text-right" [translate]>Actions</th>
						</tr>
					</thead>
					<tbody>
						@for (job of jobs(); track trackByJobId($index, job)) {
							<tr
								class="hover:bg-base-200/30 cursor-pointer align-top"
								[class.bg-base-200/40]="expandedJobId() === job.id"
								(click)="toggleExpandJob(job)"
								(keyup.enter)="toggleExpandJob(job)"
								[attr.aria-expanded]="expandedJobId() === job.id"
								tabindex="0"
							>
								<td class="whitespace-nowrap font-mono text-sm">{{ job.schedule }}</td>
								<td class="max-w-xs">
									<code class="text-xs break-all">{{ job.command }}</code>
								</td>
								<td class="text-sm">{{ job.comment ?? '–' }}</td>
								<td>
									@if (job.logFile) {
										<code class="text-xs opacity-60">{{ job.logFile }}</code>
									} @else {
										<span class="text-base-content/40 text-xs">–</span>
									}
								</td>
								<td class="text-right">
									<div class="flex justify-end gap-1">
										<button
											class="btn btn-ghost btn-xs"
											[disabled]="runningJobId() === job.id"
											(click)="$event.stopImmediatePropagation(); runJob(job)"
										>
											@if (runningJobId() === job.id) {
												<span class="loading loading-spinner loading-xs"></span>
											} @else {
												<span [translate]>Run</span>
											}
										</button>
										@if (job.logFile) {
											<button
												class="btn btn-ghost btn-xs"
												[disabled]="loadingLogsJobId() === job.id"
												(click)="$event.stopImmediatePropagation(); loadLogs(job)"
											>
												@if (loadingLogsJobId() === job.id) {
													<span class="loading loading-spinner loading-xs"></span>
												} @else {
													<span [translate]>Logs</span>
												}
											</button>
										}
										<button
											class="btn btn-ghost btn-xs text-error"
											[disabled]="!!deletingJobId()"
											(click)="$event.stopImmediatePropagation(); removeJob(job)"
										>
											@if (deletingJobId() === job.id) {
												<span class="loading loading-spinner loading-xs"></span>
											} @else {
												<span [translate]>Remove</span>
											}
										</button>
									</div>
								</td>
							</tr>

							<!-- Expanded row: run result or logs -->
							@if (expandedJobId() === job.id) {
								<tr>
									<td
										colspan="5"
										class="p-0"
									>
										<div class="bg-base-200/40 border-base-200 border-t p-4">
											<div class="mb-3 flex items-center justify-end">
												<button
													class="btn btn-ghost btn-xs"
													(click)="$event.stopImmediatePropagation(); closeExpandedJob()"
												>
													<span [translate]>Close</span>
												</button>
											</div>
											@if (jobRunResult(); as runResult) {
												<div class="flex flex-col gap-3">
													<div class="flex items-center gap-2">
														<span class="text-sm font-semibold" [translate]>Run result</span>
														<span
															class="badge badge-sm"
															[class.badge-success]="runResult.success"
															[class.badge-error]="!runResult.success"
														>
															@if (runResult.success) {
																<span [translate]>success</span>
															} @else {
																<span [translate]>failed</span>
															}
														</span>
														<span class="text-base-content/50 text-xs">
															<span [translate]>exit</span>
															{{ runResult.returnCode }} · {{ runResult.durationSeconds }}s
														</span>
													</div>
													@if (runResult.stdout) {
														<div>
															<div
																class="text-base-content/60 mb-1 text-xs font-semibold uppercase"
																[translate]
															>
																stdout
															</div>
															<pre
																class="max-h-64 overflow-auto p-3 rounded-lg border border-base-200 bg-base-100 text-xs whitespace-pre-wrap"
																>{{ runResult.stdout }}</pre
															>
														</div>
													}
													@if (runResult.stderr) {
														<div>
															<div
																class="text-error/70 mb-1 text-xs font-semibold uppercase"
																[translate]
															>
																stderr
															</div>
															<pre
																class="max-h-64 overflow-auto p-3 rounded-lg border border-error/20 bg-base-100 text-xs whitespace-pre-wrap"
																>{{ runResult.stderr }}</pre
															>
														</div>
													}
												</div>
											} @else if (jobLogs() !== null) {
												<div>
													<div
														class="text-base-content/60 mb-1 text-xs font-semibold uppercase"
														[translate]
													>
														Log file (last 200 lines)
													</div>
													@if (jobLogs()) {
														<pre
															class="max-h-96 overflow-auto p-3 rounded-lg border border-base-200 bg-base-100 text-xs whitespace-pre-wrap"
															>{{ jobLogs() }}</pre
														>
													} @else {
														<p
															class="text-base-content/60 text-sm"
															[translate]
														>
															Log file is empty or does not exist yet.
														</p>
													}
												</div>
											} @else {
												<p
													class="text-base-content/60 text-sm"
													[translate]
												>
													Click Run to execute this job and see its output here, or click Logs to view the log
													file.
												</p>
											}
										</div>
									</td>
								</tr>
							}
						}
					</tbody>
				</table>
			</div>
		}
	</div>
</div>
